// ===================================
// TRENCH QUEST - Voice Controls Component
// TTS/STT controls for voice mode
// ===================================

import { FC } from 'react'

interface VoiceControlsProps {
  isSpeaking: boolean
  isListening: boolean
  isVoiceModeEnabled: boolean
  onStopSpeaking: () => void
  onStartListening: () => void
  onStopListening: () => void
  transcript?: string
}

export const VoiceControls: FC<VoiceControlsProps> = ({
  isSpeaking,
  isListening,
  isVoiceModeEnabled,
  onStopSpeaking,
  onStartListening,
  onStopListening,
  transcript,
}) => {
  if (!isVoiceModeEnabled) return null

  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-game-panel/50 border border-game-border rounded">
      {/* TTS indicator/control */}
      {isSpeaking && (
        <button
          onClick={onStopSpeaking}
          className="flex items-center gap-1.5 px-2 py-1 bg-game-accent/20 border border-game-accent/50 rounded text-game-accent hover:bg-game-accent/30 transition-colors"
          title="Stop narration"
        >
          <SpeakerWaveIcon className="w-4 h-4 animate-pulse" />
          <span className="font-pixel text-xs">SPEAKING...</span>
          <StopIcon className="w-3 h-3" />
        </button>
      )}

      {/* STT control */}
      {!isSpeaking && (
        <button
          onClick={isListening ? onStopListening : onStartListening}
          className={`
            flex items-center gap-1.5 px-2 py-1 border rounded transition-colors
            ${isListening
              ? 'bg-red-500/20 border-red-500/50 text-red-400 hover:bg-red-500/30'
              : 'bg-game-panel border-game-border text-game-text hover:bg-game-border/50'
            }
          `}
          title={isListening ? 'Stop listening' : 'Start voice input'}
        >
          <MicrophoneIcon className={`w-4 h-4 ${isListening ? 'animate-pulse' : ''}`} />
          <span className="font-pixel text-xs">
            {isListening ? 'LISTENING...' : 'VOICE'}
          </span>
        </button>
      )}

      {/* Transcript preview */}
      {isListening && transcript && (
        <div className="flex-1 min-w-0">
          <p className="font-retro text-sm text-game-muted truncate italic">
            "{transcript}"
          </p>
        </div>
      )}
    </div>
  )
}

// Inline SVG icons
const SpeakerWaveIcon: FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
  </svg>
)

const MicrophoneIcon: FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
  </svg>
)

const StopIcon: FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <rect x="6" y="6" width="12" height="12" rx="1" />
  </svg>
)
